// Centralized API service for lead management and geolocation

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object|null>} Geolocation data or null if failed
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error(`Geolocation API failed: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.isSuccess && data.data) {
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching geolocation:', error);
    return null;
  }
};

/**
 * Extract numeric phone number from formatted string
 * Removes all non-numeric characters
 * @param {string} phoneNumber - Phone number string (may include formatting)
 * @returns {string} Numeric only phone number
 */
export const sanitizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Submit lead data to API
 * @param {Object} formData - Form data object
 * @param {string} formData.name - Customer name
 * @param {string} formData.email - Customer email
 * @param {string} formData.phoneNumber - Customer phone number (will be sanitized)
 * @param {string} formData.countryDialCode - Country dial code (numeric)
 * @param {string} formData.country - Country code (e.g., 'US', 'PK')
 * @param {string} formData.description - Lead description
 * @param {string} [formData.packageTitle] - Optional package title
 * @param {number} [formData.packageAmount] - Optional package amount
 * @param {Object} [geoLocationData] - Optional geolocation data from fetchGeolocation
 * @returns {Promise<Object>} API response with success/error information
 */
export const submitLead = async (formData, geoLocationData = null) => {
  try {
    // Get current page URL
    const pageUrl = window.location.href;
    
    // Sanitize phone number to numeric only
    const numericPhone = sanitizePhoneNumber(formData.phoneNumber);
    
    // Build payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryDialCode) || 1,
      country: formData.country || 'US',
      phoneNumber: numericPhone,
      ipAddress: geoLocationData?.data?.ipAddress || '0.0.0.0',
      description: formData.description || '',
      pageUrl: pageUrl,
      city: geoLocationData?.data?.cityName || '',
      state: geoLocationData?.data?.regionName || '',
      geoLocation: geoLocationData ? JSON.stringify(geoLocationData) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: parseInt(formData.packageAmount) || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle different status codes
    if (response.status === 200 && data.isSuccess && data.data?.customerId) {
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message || 'Lead created successfully',
        data: data
      };
    }

    if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized',
        statusCode: 401,
        data: data
      };
    }

    if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Bad request',
        statusCode: 400,
        data: data
      };
    }

    // Handle any other status codes
    return {
      success: false,
      error: data.errorMessage || 'An error occurred',
      statusCode: response.status,
      data: data
    };

  } catch (error) {
    console.error('Error submitting lead:', error);
    return {
      success: false,
      error: 'Network error. Please try again.',
      statusCode: 0
    };
  }
};

/**
 * Get country list with dial codes
 * Returns commonly used countries
 */
export const getCountries = () => {
  return [
    { code: 'US', name: 'United States', dialCode: '1' },
    { code: 'GB', name: 'United Kingdom', dialCode: '44' },
    { code: 'CA', name: 'Canada', dialCode: '1' },
    { code: 'AU', name: 'Australia', dialCode: '61' },
    { code: 'IN', name: 'India', dialCode: '91' },
    { code: 'PK', name: 'Pakistan', dialCode: '92' },
    { code: 'AE', name: 'UAE', dialCode: '971' },
    { code: 'SA', name: 'Saudi Arabia', dialCode: '966' },
    { code: 'DE', name: 'Germany', dialCode: '49' },
    { code: 'FR', name: 'France', dialCode: '33' },
    { code: 'IT', name: 'Italy', dialCode: '39' },
    { code: 'ES', name: 'Spain', dialCode: '34' },
    { code: 'NL', name: 'Netherlands', dialCode: '31' },
    { code: 'BE', name: 'Belgium', dialCode: '32' },
    { code: 'CH', name: 'Switzerland', dialCode: '41' },
    { code: 'SE', name: 'Sweden', dialCode: '46' },
    { code: 'NO', name: 'Norway', dialCode: '47' },
    { code: 'DK', name: 'Denmark', dialCode: '45' },
    { code: 'FI', name: 'Finland', dialCode: '358' },
    { code: 'PL', name: 'Poland', dialCode: '48' },
    { code: 'AT', name: 'Austria', dialCode: '43' },
    { code: 'IE', name: 'Ireland', dialCode: '353' },
    { code: 'NZ', name: 'New Zealand', dialCode: '64' },
    { code: 'SG', name: 'Singapore', dialCode: '65' },
    { code: 'MY', name: 'Malaysia', dialCode: '60' },
    { code: 'TH', name: 'Thailand', dialCode: '66' },
    { code: 'PH', name: 'Philippines', dialCode: '63' },
    { code: 'ID', name: 'Indonesia', dialCode: '62' },
    { code: 'VN', name: 'Vietnam', dialCode: '84' },
    { code: 'JP', name: 'Japan', dialCode: '81' },
    { code: 'KR', name: 'South Korea', dialCode: '82' },
    { code: 'CN', name: 'China', dialCode: '86' },
    { code: 'HK', name: 'Hong Kong', dialCode: '852' },
    { code: 'TW', name: 'Taiwan', dialCode: '886' },
    { code: 'BR', name: 'Brazil', dialCode: '55' },
    { code: 'MX', name: 'Mexico', dialCode: '52' },
    { code: 'AR', name: 'Argentina', dialCode: '54' },
    { code: 'CL', name: 'Chile', dialCode: '56' },
    { code: 'CO', name: 'Colombia', dialCode: '57' },
    { code: 'ZA', name: 'South Africa', dialCode: '27' },
    { code: 'NG', name: 'Nigeria', dialCode: '234' },
    { code: 'KE', name: 'Kenya', dialCode: '254' },
    { code: 'EG', name: 'Egypt', dialCode: '20' },
    { code: 'TR', name: 'Turkey', dialCode: '90' },
    { code: 'RU', name: 'Russia', dialCode: '7' },
    { code: 'IL', name: 'Israel', dialCode: '972' },
    { code: 'GR', name: 'Greece', dialCode: '30' },
    { code: 'PT', name: 'Portugal', dialCode: '351' },
    { code: 'CZ', name: 'Czech Republic', dialCode: '420' },
    { code: 'HU', name: 'Hungary', dialCode: '36' }
  ];
};

/**
 * Find country by country code
 * @param {string} countryCode - 2-letter country code
 * @returns {Object|null} Country object or null
 */
export const findCountryByCode = (countryCode) => {
  const countries = getCountries();
  return countries.find(c => c.code === countryCode) || null;
};
